import { httpService } from '../utility/httpService';
import { httpConstants } from '../constants';
import { API_BASE, API_GRAPH_BASE } from '../config/env';

/**
 * Update process description and KPIs
 */
export const updateProcessKPIs = async (
  processId: string,
  processDescription: string,
  kpis: any
): Promise<boolean> => {
  const url = `${API_GRAPH_BASE}/custom-process/subprocess/${processId}`;
  try {
    const response = await httpService<any>(httpConstants.METHOD_TYPE.PUT, url, {
      process_description: processDescription,
      kpis: kpis,
    });
    return !!response;
  } catch (error) {
    console.error('KPI update failed:', error);
    return false;
  }
};

/**
 * Update process name
 */
export const updateProcessName = async (processId: string, newName: string): Promise<boolean> => {
  try {
    const url = `${API_BASE}/api/v1/e2e_taxonomy/level2/${processId}/`;
    const payload = { name: newName };
    await httpService<any>(httpConstants.METHOD_TYPE.PATCH, url, payload);
    return true;
  } catch (error) {
    console.error('Error updating process name:', error);
    return false;
  }
};

/**
 * Delete custom process
 */
export const deleteCustomProcess = async (
  organizationId: string,
  processId: string
): Promise<boolean> => {
  try {
    const response = await httpService<any>(
      httpConstants.METHOD_TYPE.DELETE,
      `${API_GRAPH_BASE}/custom-process/organization/${organizationId}/custom-taxonomy/${processId}`
    );

    if (response && (response.success === true || response.status === 200 || !response.error)) {
      console.log('Process deleted successfully');
      return true;
    } else {
      console.error('DELETE API Error:', response?.error || 'Unknown error');
      return false;
    }
  } catch (error) {
    console.error('Failed to delete process:', error);
    return false;
  }
};

/**
 * Delete ideation/use case
 */
export const deleteIdeation = async (useCaseId: string): Promise<boolean> => {
  try {
    const url = `${API_BASE}/api/v1/ideation/use-cases/${useCaseId}/delete/`;
    const response = await httpService<any>(httpConstants.METHOD_TYPE.DELETE, url);

    if (response) {
      console.log('Ideation deleted successfully');
      return true;
    } else {
      console.error('Failed to delete ideation');
      return false;
    }
  } catch (error) {
    console.error('Error deleting ideation:', error);
    return false;
  }
};

/**
 * Update vault status for AI solution
 */
export const updateVaultStatus = async (
  organizationId: string,
  aiSolutionId: string,
  addToVault: boolean
): Promise<boolean> => {
  const url = `${API_BASE}/api/v1/e2e_taxonomy/new-vault/`;
  try {
    const response = await httpService<any>(
      addToVault ? httpConstants.METHOD_TYPE.POST : httpConstants.METHOD_TYPE.DELETE,
      url,
      {
        organization_id: organizationId,
        ai_solution_id: aiSolutionId,
      }
    );
    return !!response;
  } catch (error) {
    console.error('Vault update failed:', error);
    return false;
  }
};

/**
 * Fetch ideation details
 */
export const fetchIdeationDetails = async (useCaseId: string): Promise<any> => {
  try {
    const ideationDetails = await httpService<any>(
      httpConstants.METHOD_TYPE.GET,
      `${API_BASE}/api/v1/ideation/use-cases/${useCaseId}/`
    );
    return ideationDetails;
  } catch (error) {
    console.error('Error fetching ideation details:', error);
    throw error;
  }
};

/**
 * Fetch AI opportunities by diagram ID
 */
export const fetchAIOpportunitiesByDiagram = async (diagramId: string): Promise<any> => {
  try {
    const response = await httpService<{ results: any[] }>(
      httpConstants.METHOD_TYPE.GET,
      `${API_BASE}/api/v1/e2e_taxonomy/opportunities/?diagram_id=${diagramId}`
    );
    return response;
  } catch (error) {
    console.error('Error fetching AI opportunities:', error);
    throw error;
  }
};

/**
 * Check process status (for polling/regeneration scenarios)
 */
export const checkProcessStatus = async (processId: string): Promise<string> => {
  try {
    const response = await httpService<{
      id: string;
      name: string;
      status: string;
      diagram: Record<string, any>;
      aisolution: Record<string, any>;
    }>(
      httpConstants.METHOD_TYPE.GET,
      `${API_BASE}/api/v1/e2e_taxonomy/level2/${processId}/status/`,
      {}
    );
    return response?.status || '';
  } catch (error) {
    console.error('Error checking status:', error);
    return '';
  }
};

/**
 * Update process status
 */
export const updateProcessStatus = async (processId: string, status: string): Promise<boolean> => {
  try {
    await httpService<any>(
      httpConstants.METHOD_TYPE.POST,
      `${API_BASE}/api/v1/e2e_taxonomy/level2/${processId}/status/`,
      { status }
    );
    return true;
  } catch (error) {
    console.error('Error updating process status:', error);
    return false;
  }
};
